import { useSessionStore } from "@/stores/session";

type TokenAccessor = () => Promise<string>;

let _getToken: TokenAccessor | null = null;

export function setTokenAccessor(fn: TokenAccessor): void {
  _getToken = fn;
}

async function getToken(): Promise<string> {
  if (!_getToken) {
    throw new Error(
      "Token accessor not initialized. Was Auth0Provider mounted?",
    );
  }
  return _getToken();
}

export class ApiError extends Error {
  status: number;
  body: string;

  constructor(status: number, body: string) {
    super(`API Error ${status}: ${body}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

type ApiFetchOptions = {
  method?: string;
  body?: string;
  headers?: Record<string, string>;
};

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const token = await getToken();
  const tenantId = useSessionStore.getState().currentTenantId;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers ?? {}),
  };

  if (tenantId) {
    headers["X-Tenant-Id"] = tenantId;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }

  return response.json() as Promise<T>;
}
